import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { ref } from 'vue';
import { uuid4 } from '~~/common/utils';
import { useToasterStore } from '~~/common/stores/toaster.js';
import { notificationTokenizer, type NotificationText } from '~~/common/utils/helper';

/**
 * The action buttons a notification can offer, keyed by the outcome they stand for. `AwNotificationItem`
 * renders one button per key and calls `cb` with that key, which is how the caller's promise learns
 * which button was pressed.
 */
type NotificationPromise = Record<string, {
  cb?: (outcome: string) => void;
  styling?: string;
  textToken?: string;
  textString?: string;
}>;

interface Notification {
  type: 'warning' | 'info' | 'error' | 'success';
  manualClose?: boolean;
  screenReaderOnly?: boolean;
  text: NotificationText;
  promise?: NotificationPromise | null;
  uuid?: string;
}

let nextIndexCounter = 0;
export const useNotificationStore = defineStore('notification', () => {
  const nuxtApp = useNuxtApp();
  const toasterStore = useToasterStore();
  const notifications = ref<Record<number, Notification>>({});

  function setNotification (indexCounter: number, notification: Notification) {
    const uuid = uuid4();
    notifications.value[indexCounter] = { ...notification, uuid };
  }

  function pushWarning ({ text, manualClose, screenReaderOnly, promise }: { text: NotificationText, manualClose?: boolean, screenReaderOnly?: boolean, promise?: NotificationPromise }) {
    setNotification(nextIndexCounter++, {
      type: 'warning',
      manualClose,
      screenReaderOnly,
      text,
      promise,
    });
  }

  function pushInfo ({ text, manualClose, screenReaderOnly, promise }: { text: NotificationText, manualClose?: boolean, screenReaderOnly?: boolean, promise?: NotificationPromise }) {
    setNotification(nextIndexCounter++, {
      type: 'info',
      manualClose,
      screenReaderOnly,
      text,
      promise,
    });
  }

  function pushError ({ text, manualClose, screenReaderOnly, promise }: { text: NotificationText, manualClose?: boolean, screenReaderOnly?: boolean, promise?: NotificationPromise | null }) {
    const { $awt } = nuxtApp;
    const notification = notificationTokenizer({ text }, { awt: $awt });

    if (typeof text?.title !== 'string' && text?.title?.message?.includes('Request aborted')) {
      return;
    }
    if (typeof text?.title !== 'string' && text?.title?.message?.includes('Network Error')) {
      toasterStore.push({
        title: $awt('aw.network_error'),
        text: notification.subtitle,
        manualClose,
        screenReaderOnly,
        promise,
      });

    } else {
      toasterStore.push({
        title: notification.title,
        text: notification.subtitle,
        manualClose,
        screenReaderOnly,
        promise,
      });
    }
  }

  function pushSuccess ({ text, manualClose, screenReaderOnly, promise }: { text: NotificationText, manualClose?: boolean, screenReaderOnly?: boolean, promise?: NotificationPromise }) {
    const { $awt } = nuxtApp;
    const notification = notificationTokenizer({ text }, { awt: $awt });
    toasterStore.push({
      title: notification.title,
      text: notification.subtitle,
      manualClose,
      screenReaderOnly,
      promise,
    });
  }

  function remove (key: number) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete notifications.value[key];
  }

  return {
    notifications,
    setNotification,
    pushError,
    pushWarning,
    pushSuccess,
    pushInfo,
    remove,
  };
});
